import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  getAdminNotifications,
  EMPTY_NOTIFICATIONS,
  type AdminNotifications,
} from '../lib/notifications-api';
import { useT } from '../i18n';

const LAST_SEEN_KEY = 'livinhome.admin.notifLastSeen';
const POLL_MS = 60_000;

interface Row {
  key: keyof AdminNotifications;
  label: string;
  to: string;
  urgent?: boolean;
}

const ROWS: Row[] = [
  { key: 'emergenciesActive', label: 'notif.emergencies', to: '/operations', urgent: true },
  { key: 'bookingsPending', label: 'notif.bookingsPending', to: '/bookings' },
  { key: 'complaintsNew', label: 'notif.complaintsNew', to: '/complaints' },
  { key: 'packagesNew', label: 'notif.packagesNew', to: '/operations' },
  { key: 'scansNew', label: 'notif.scansNew', to: '/operations' },
];

export function NotificationBell() {
  const t = useT();
  const [counts, setCounts] = useState<AdminNotifications>(EMPTY_NOTIFICATIONS);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const lastSeenRef = useRef<string>(
    localStorage.getItem(LAST_SEEN_KEY) ?? new Date().toISOString(),
  );

  useEffect(() => {
    let active = true;
    const refresh = () => {
      getAdminNotifications(lastSeenRef.current)
        .then((c) => {
          if (active) setCounts(c);
        })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    // Opening the bell marks informational items (packages / check-ins) as seen.
    const now = new Date().toISOString();
    lastSeenRef.current = now;
    localStorage.setItem(LAST_SEEN_KEY, now);
    setCounts((c) => ({
      ...c,
      packagesNew: 0,
      scansNew: 0,
      total: c.emergenciesActive + c.bookingsPending + c.complaintsNew,
    }));
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="notifications" size="small">
        <Badge badgeContent={counts.total} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 320 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ px: 2, pt: 1.5, pb: 1 }}>
            {t('notif.title')}
          </Typography>
          <Divider />
          {counts.total === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t('notif.empty')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {ROWS.map((r) => {
                const n = counts[r.key];
                if (n <= 0) return null;
                return (
                  <ListItemButton
                    key={r.key}
                    component={RouterLink}
                    to={r.to}
                    onClick={() => setAnchorEl(null)}
                  >
                    <ListItemText primary={t(r.label)} />
                    <Chip size="small" label={n} color={r.urgent ? 'error' : 'primary'} />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
